import type { FitnessDomain, WeeklyCommitment } from '~/models/fitnessDomain'
import { FITNESS_DOMAIN_LABELS, isFitnessDomain } from '~/models/fitnessDomain'
import { weekAround } from './weekShape'

/**
 * Turning a reviewer's suggestion into something the editor can be filled with
 * (D-093 §6).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS PRODUCES A DRAFT. IT NEVER PRODUCES A RECORD
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The output goes into My Plan's commitment editor as its starting values and
 *  nowhere else. **The user reviews it and saves it themselves**, through the one
 *  `setWeeklyCommitment` path, and only then does anything reach storage. That is
 *  the constraint that keeps a reviewer a reviewer, and D-093 §6 states its honest
 *  cost rather than burying it: a pre-filled field is usually just saved.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE SUGGESTION SUPPLIES A COUNT. SOLA SUPPLIES THE SHAPE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A `WeeklyCommitmentSuggestion` carries `sessionsPerWeek` and an optional
 *  `minutesPerSession`, and that is the whole of the wire type. The short week and
 *  the floor come from `weekAround`, the same function the setup starter uses.
 *
 *  **The floor is the part that matters.** `minimumViable` is the safety valve
 *  that makes a bad week survivable rather than failed, and a model setting it
 *  would be making a coaching decision behind a field the user reads as
 *  descriptive. Sola derives it from the count, always.
 *
 *  **Nothing here re-checks the allowlist.** `SUGGESTION_KINDS` is
 *  `weeklyCommitment` alone, the Worker rejects anything else, and
 *  `validateAiProposal` refuses to store a suggestion outside it — a fourth guard
 *  would be the weakest of the four. What this file does check is that the domain
 *  is one this build knows and one the user is actually planning, because that is
 *  a property of *this device's* records rather than of the reply.
 */

/** The parts of a stored suggestion this needs. Structural, not the wire type. */
export interface SuggestedCommitmentInput {
  readonly domain: string
  readonly sessionsPerWeek: number
  readonly minutesPerSession?: number | undefined
}

export interface SuggestedCommitment {
  readonly domain: FitnessDomain
  readonly commitment: WeeklyCommitment
}

/**
 * The commitment a suggestion implies, or nothing when it cannot be honoured.
 *
 * `undefined` for a domain this build does not recognise and for a session count
 * that is not a whole number of at least one — **rounded up would invent a
 * session nobody proposed**, and zero is not a plan. A caller showing an editor
 * for a suggestion that produced nothing would be offering the user the reviewer's
 * name attached to Sola's own defaults.
 */
export function suggestedCommitment(
  input: SuggestedCommitmentInput,
): SuggestedCommitment | undefined {
  if (!isFitnessDomain(input.domain)) return undefined
  /* Body composition is not trained in sessions — its plan is the phase and its
     weekly evidence is the derived checklist (D-101 §4). "Body composition
     sessions: 3 a week" is not a plan anyone can act on, and the Worker cannot
     know that: its own check is only that the domain is active, which this one
     is. This is a different rule from the allowlist, not a second guard for it. */
  if (input.domain === 'bodyComposition') return undefined

  const timesPerWeek = Math.floor(input.sessionsPerWeek)
  if (!Number.isFinite(timesPerWeek) || timesPerWeek < 1) return undefined

  const minutes = input.minutesPerSession
  const usable = minutes !== undefined && Number.isFinite(minutes) && minutes > 0

  const item = {
    /* The same label shape the starter plan and the week checklist build, so a
       suggested item and an existing one read as the same kind of thing rather
       than as two features. */
    label: `${FITNESS_DOMAIN_LABELS[input.domain]} sessions`,
    timesPerWeek,
    ...(usable ? { minutes: Math.round(minutes) } : {}),
  }

  return { domain: input.domain, commitment: weekAround(item) }
}
